import React, { useState, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  CreditCard, 
  CurrencyEur,
  CheckCircle,
  Clock,
  XCircle,
  ArrowsClockwise,
  Calendar,
  Receipt
} from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  paid: { label: 'Payé', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-800', icon: XCircle },
  refunded: { label: 'Remboursé', color: 'bg-purple-100 text-purple-800', icon: ArrowsClockwise },
};

const PaymentsPage = () => {
  const { currentStore } = useStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!currentStore) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/stores/${currentStore.id}/orders`, {
          withCredentials: true
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentStore]);

  if (!currentStore) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Sélectionnez une boutique pour voir les paiements</p>
      </div>
    );
  }

  // Calculate totals
  const paidOrders = orders.filter(o => o.payment_status === 'paid');
  const pendingOrders = orders.filter(o => o.payment_status === 'pending');
  const totalRevenue = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);
  const pendingRevenue = pendingOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div>
        <h1 className="font-cabinet text-3xl font-bold tracking-tight">Paiements</h1>
        <p className="text-slate-500 mt-1">Suivez vos transactions et revenus</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Revenus encaissés</p>
                <p className="text-3xl font-bold font-cabinet text-emerald-600">{totalRevenue.toFixed(2)} €</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                <CurrencyEur size={24} className="text-white" weight="fill" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-3xl font-bold font-cabinet text-amber-600">{pendingRevenue.toFixed(2)} €</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                <Clock size={24} className="text-white" weight="fill" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Transactions payées</p>
                <p className="text-3xl font-bold font-cabinet">{paidOrders.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                <CheckCircle size={24} className="text-white" weight="fill" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="dashboard-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total transactions</p>
                <p className="text-3xl font-bold font-cabinet">{orders.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center">
                <Receipt size={24} className="text-white" weight="fill" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle>Historique des transactions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <CreditCard size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucune transaction pour le moment</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Méthode</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => {
                  const status = statusConfig[order.payment_status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  
                  return (
                    <TableRow key={order.id} data-testid={`payment-row-${order.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.order_number}</p>
                          <p className="text-sm text-slate-500">{order.items?.length || 0} article(s)</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">
                          {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{order.customer_email}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CreditCard size={16} className="text-slate-400" />
                          <span className="capitalize">{order.payment_method}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">{order.total?.toFixed(2)} €</TableCell>
                      <TableCell>
                        <Badge className={status.color}>
                          <StatusIcon size={14} className="mr-1" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar size={14} />
                          {new Date(order.created_at).toLocaleDateString('fr-FR')}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
